/**
 * Service for managing optimistic messages in localStorage
 * Implements optimistic visualization for sent messages
 */

const OPTIMISTIC_MESSAGES_KEY = 'optimistic_messages';

/**
 * Generates a unique ID for optimistic messages
 */
const generateOptimisticId = () => {
    return `optimistic_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Creates an optimistic message based on the data provided
 */
const createOptimisticMessage = (content, contentType, chatId, sectorId, sign, operatorName = 'Operador') => {
    const now = new Date();
    const timestamp = Math.floor(now.getTime() / 1000);
    
    const isMedia = ['image', 'video', 'audio', 'document'].includes(contentType);
    
    // Formats the content with the operator name, same as the backend
    const formattedContent = isMedia ? '' : `*${operatorName}*\n${content}`;
    
    return {
        key: {
            id: generateOptimisticId(),
            fromMe: true,
            remoteJid: `${chatId}@s.whatsapp.net`
        },
        message: {
            conversation: null,
            extendedTextMessage: {
                text: formattedContent
            }
        },
        messageTimestamp: {
            low: timestamp,
            high: 0
        },
        sent_at: now,
        contentType: contentType,
        isOptimistic: true,
        chatId: chatId,
        sectorId: sectorId,
        sign: sign
    };
};

/**
 * Saves an optimistic message to localStorage
 */
export const saveOptimisticMessage = (content, contentType, chatId, sectorId, sign, operatorName = 'Operador') => {
    try {
        const optimisticMessage = createOptimisticMessage(content, contentType, chatId, sectorId, sign, operatorName);
        const existingMessages = getOptimisticMessages();
        
        // Adds the new optimistic message
        const updatedMessages = [...existingMessages, optimisticMessage];
        
        localStorage.setItem(OPTIMISTIC_MESSAGES_KEY, JSON.stringify(updatedMessages));
        
        return optimisticMessage;
    } catch (error) {
        console.error('Error saving optimistic message:', error);
        return null;
    }
};

/**
 * Retrieves all optimistic messages from localStorage
 */
export const getOptimisticMessages = () => {
    try {
        const messages = localStorage.getItem(OPTIMISTIC_MESSAGES_KEY);
        if (!messages) {
            return [];
        }
        const parsedMessages = JSON.parse(messages);
        // Ensure sent_at is a Date object
        const mappedMessages = parsedMessages.map(msg => ({
            ...msg,
            sent_at: new Date(msg.sent_at),
        }));
        return mappedMessages;
    } catch (error) {
        console.error('Error retrieving optimistic messages:', error);
        return [];
    }
};

/**
 * Retrieves optimistic messages for a specific chat
 */
export const getOptimisticMessagesForChat = (chatId, sectorId) => {
    try {
        const allMessages = getOptimisticMessages();
        return allMessages.filter(msg => 
            msg.chatId == chatId && msg.sectorId == sectorId
        );
    } catch (error) {
        console.error('Error retrieving optimistic chat messages:', error);
        return [];
    }
};

/**
 * Remove a specific optimistic message from localStorage
 */
export const removeOptimisticMessage = (messageId) => {
    try {
        const existingMessages = getOptimisticMessages();
        const updatedMessages = existingMessages.filter(msg => msg.key.id !== messageId);
        
        localStorage.setItem(OPTIMISTIC_MESSAGES_KEY, JSON.stringify(updatedMessages));
        return true;
    } catch (error) {
        console.error('Error removing optimistic message:', error);
        return false;
    }
};

/**
 * Remove all optimistic messages from a specific chat
 */
export const clearOptimisticMessagesForChat = (chatId, sectorId) => {
    try {
        const existingMessages = getOptimisticMessages();
        const updatedMessages = existingMessages.filter(msg => 
            !(msg.chatId === chatId && msg.sectorId === sectorId)
        );
        
        localStorage.setItem(OPTIMISTIC_MESSAGES_KEY, JSON.stringify(updatedMessages));
        return true;
    } catch (error) {
        console.error('Error clearing optimistic chat messages:', error);
        return false;
    }
};

/**
 * Compares two messages to verify they are the same
 * Compares by content and approximate timestamp (within 30 seconds)
 */
const isSameMessage = (optimisticMsg, backendMsg) => {
    try {
        // Checks if both are messages sent by the user
        if (!optimisticMsg.key?.fromMe || !backendMsg.key?.fromMe) {
            return false;
        }

        // Helper to extract the pure message content, ignoring the operator part
        const getPureContent = (msg) => {
            const content = msg?.conversation || msg?.extendedTextMessage?.text || '';
            const newlineIndex = content.indexOf('\n');
            // Check if there is a line break and if the first line is formatted as an operator
            if (newlineIndex !== -1 && content.startsWith('*') && content.substring(0, newlineIndex).endsWith('*')) {
                return content.substring(newlineIndex + 1);
            }
            return content;
        };

        const optimisticContent = getPureContent(optimisticMsg.message);
        const backendContent = getPureContent(backendMsg.message);

        // Now compares only the pure content of the message
        if (optimisticContent !== backendContent) {
            return false;
        }

        // Compare timestamps (within 30 seconds)
        const optimisticTimestamp = optimisticMsg.messageTimestamp?.low || optimisticMsg.messageTimestamp;
        const backendTimestamp = Number.parseInt(backendMsg.messageTimestamp?.low || backendMsg.messageTimestamp);
        const timeDiff = Math.abs(optimisticTimestamp - backendTimestamp);

        // Accepts difference of up to 30 seconds
        return timeDiff <= 30;
    } catch (error) {
        console.error('Error comparing messages:', error);
        return false;
    }
};

/**
 * Merges backend messages with optimistic messages
 * Removes optimistic messages that have already been acknowledged by the backend
 */
export const mergeMessagesWithOptimistic = (backendMessages, chatId, sectorId) => {
    try {
        const optimisticMessages = getOptimisticMessagesForChat(chatId, sectorId);
        
        // Identifies optimistic messages that have been confirmed by the backend
        const confirmedOptimisticIds = [];
        
        for (const optimisticMsg of optimisticMessages) {
            const isConfirmed = backendMessages.some(backendMsg => 
                isSameMessage(optimisticMsg, backendMsg)
            );
            
            if (isConfirmed) {
                confirmedOptimisticIds.push(optimisticMsg.key.id);
            }
        }
        
        // Removes committed optimistic messages from localStorage
        if (confirmedOptimisticIds.length > 0) {
            const existingMessages = getOptimisticMessages();
            const updatedMessages = existingMessages.filter(msg => 
                !confirmedOptimisticIds.includes(msg.key.id)
            );
            localStorage.setItem(OPTIMISTIC_MESSAGES_KEY, JSON.stringify(updatedMessages));
        }
        
        // Filters optimistic messages that have not yet been confirmed by the backend
        const remainingOptimistic = optimisticMessages.filter(msg => 
            !confirmedOptimisticIds.includes(msg.key.id)
        );
        
        // Combines messages from the backend with remaining optimistic messages
        const allMessages = [...backendMessages, ...remainingOptimistic];
        
        // Sort by timestamp
        allMessages.sort((a, b) => {
            const timestampA = a.messageTimestamp?.low || a.messageTimestamp;
            const timestampB = b.messageTimestamp?.low || b.messageTimestamp;
            return timestampA - timestampB;
        });
        
        return allMessages;
    } catch (error) {
        console.error('Error merging messages:', error);
        return backendMessages || [];
    }
};

/**
 * Removes old optimistic messages (more than 24 hours old)
 */
export const cleanupOldOptimisticMessages = () => {
    try {
        const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
        const existingMessages = getOptimisticMessages();
        
        const recentMessages = existingMessages.filter(msg => {
            const messageTime = new Date(msg.sent_at).getTime();
            return messageTime > twentyFourHoursAgo;
        });
        
        localStorage.setItem(OPTIMISTIC_MESSAGES_KEY, JSON.stringify(recentMessages));
        return true;
    } catch (error) {
        console.error('Error cleaning up old optimistic messages:', error);
        return false;
    }
};
